/**
 * =============================================================================
 * SHELL NAVIGATION CONTEXT (ADR-025 blueprint §3.1.6 — B-2)
 * =============================================================================
 *
 * The Shell-provided seam through which section content performs
 * *filtered-section navigation*: dispatch a semantic `AdminLocation`, never a
 * URL (§3.1.6 / OD-2 — the app does not own the iframe URL; zero URL writes).
 *
 * Provided ONLY by `AdminShell`. Consumers call `useShellNavigation()` and
 * treat `null` as "no shell present": under the flag-OFF legacy layout (the
 * §3.5 rollback surface) no provider exists, and the consumer MUST degrade to
 * its pre-shell rendering — the legacy surface's behaviour stays byte-identical
 * (pinned per M9). First consumer: `MediaReviewPanel`'s media-row →
 * parent-question link (B-2); the B-3 Overview tiles are the next.
 * =============================================================================
 */

import { createContext, useContext } from 'react';
import type { AdminLocation } from './location';

export interface ShellNavigation {
  /**
   * Navigate the shell to a semantic location (§3.1.6). Dispatching a
   * `questions` location carrying a `questionId` also raises an open-question
   * intent for the Questions section (M8 `questionId → question` resolution —
   * fail-safe: an id not in the currently loaded rows lands on the queue
   * normally, no error).
   */
  navigate: (location: AdminLocation) => void;
}

/**
 * Default `null` = "not rendered inside AdminShell" (legacy layout / bare
 * panel tests). Exported for the provider (`AdminShell`) only — consumers use
 * `useShellNavigation`.
 */
export const ShellNavigationContext = createContext<ShellNavigation | null>(null);

/**
 * Read the shell navigation seam, or `null` when no shell is present (the
 * flag-OFF legacy layout provides no context — consumers must render their
 * legacy, non-navigating form).
 */
export function useShellNavigation(): ShellNavigation | null {
  return useContext(ShellNavigationContext);
}
